import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FinancialManagement = ({ subscriptions = [], setSubscriptions }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState(''); // 'details', 'payout', 'suspend'

  // Filter subscriptions based on search, status, and plan
  const filteredSubscriptions = subscriptions?.filter((sub) => {
    const matchesSearch =
      sub?.merchantName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      sub?.merchantId?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' || sub?.status === statusFilter;
    const matchesPlan = planFilter === 'all' || sub?.plan === planFilter;
    return matchesSearch && matchesStatus && matchesPlan;
  });

  const handleSubscriptionAction = (subscription, action) => {
    setSelectedSubscription(subscription);
    setModalType(action);
    setIsModalOpen(true);
  };

  const confirmSubscriptionAction = () => {
    if (!selectedSubscription) return;

    if (modalType === 'suspend') {
      setSubscriptions((prev) =>
        prev?.map((sub) =>
          sub?.id === selectedSubscription?.id
            ? { ...sub, status: 'suspended', nextBilling: null }
            : sub,
        ),
      );
    } else if (modalType === 'activate') {
      const nextMonth = new Date();
      nextMonth?.setMonth(nextMonth?.getMonth() + 1);
      setSubscriptions((prev) =>
        prev?.map((sub) =>
          sub?.id === selectedSubscription?.id
            ? {
                ...sub,
                status: 'active',
                nextBilling: nextMonth?.toISOString()?.split('T')?.[0],
              }
            : sub,
        ),
      );
    } else if (modalType === 'payout') {
      setSubscriptions((prev) =>
        prev?.map((sub) =>
          sub?.id === selectedSubscription?.id
            ? {
                ...sub,
                pendingPayout: 0,
                lastPayout: new Date()?.toISOString()?.split('T')?.[0],
              }
            : sub,
        ),
      );
    }

    setIsModalOpen(false);
    setSelectedSubscription(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'suspended':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'trial':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPlanColor = (plan) => {
    switch (plan?.toLowerCase()) {
      case 'basic':
        return 'text-gray-600 bg-gray-50';
      case 'professional':
        return 'text-blue-600 bg-blue-50';
      case 'enterprise':
        return 'text-purple-600 bg-purple-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const calculateStats = () => {
    const activeSubscriptions =
      subscriptions?.filter((sub) => sub?.status === 'active') || [];
    return {
      total: subscriptions?.length || 0,
      active: activeSubscriptions?.length || 0,
      suspended:
        subscriptions?.filter((sub) => sub?.status === 'suspended')?.length ||
        0,
      totalRevenue:
        subscriptions?.reduce(
          (sum, sub) => sum + (sub?.totalRevenue || 0),
          0,
        ) || 0,
      monthlyRecurring:
        activeSubscriptions?.reduce(
          (sum, sub) => sum + (sub?.monthlyFee || 0),
          0,
        ) || 0,
      pendingPayouts:
        subscriptions?.reduce(
          (sum, sub) => sum + (sub?.pendingPayout || 0),
          0,
        ) || 0,
    };
  };

  const stats = calculateStats();

  return (
    <div className='flex-1 overflow-hidden bg-background'>
      {/* Header Section */}
      <div className='bg-card border-b border-border p-6'>
        <div className='flex items-center justify-between mb-4'>
          <div>
            <h2 className='text-2xl font-bold text-foreground'>
              Financial Management
            </h2>
            <p className='text-sm text-muted-foreground'>
              Manage subscriptions, commissions, and payouts
            </p>
          </div>
          <Button className='flex items-center space-x-2'>
            <Icon name='Download' size={16} />
            <span>Export Report</span>
          </Button>
        </div>

        {/* Stats Cards */}
        <div className='grid grid-cols-6 gap-4 mb-6'>
          <div className='bg-background border border-border rounded-lg p-4'>
            <div className='text-2xl font-bold text-foreground'>
              {stats?.total}
            </div>
            <div className='text-sm text-muted-foreground'>Total Merchants</div>
          </div>
          <div className='bg-background border border-border rounded-lg p-4'>
            <div className='text-2xl font-bold text-green-600'>
              {stats?.active}
            </div>
            <div className='text-sm text-muted-foreground'>Active Plans</div>
          </div>
          <div className='bg-background border border-border rounded-lg p-4'>
            <div className='text-2xl font-bold text-red-600'>
              {stats?.suspended}
            </div>
            <div className='text-sm text-muted-foreground'>Suspended</div>
          </div>
          <div className='bg-background border border-border rounded-lg p-4'>
            <div className='text-2xl font-bold text-foreground'>
              ${(stats?.totalRevenue / 1000)?.toFixed(1)}K
            </div>
            <div className='text-sm text-muted-foreground'>Total Revenue</div>
          </div>
          <div className='bg-background border border-border rounded-lg p-4'>
            <div className='text-2xl font-bold text-blue-600'>
              ${stats?.monthlyRecurring?.toFixed(0)}
            </div>
            <div className='text-sm text-muted-foreground'>
              Monthly Recurring
            </div>
          </div>
          <div className='bg-background border border-border rounded-lg p-4'>
            <div className='text-2xl font-bold text-purple-600'>
              ${stats?.pendingPayouts?.toFixed(0)}
            </div>
            <div className='text-sm text-muted-foreground'>Pending Payouts</div>
          </div>
        </div>

        {/* Filters */}
        <div className='flex items-center space-x-4'>
          <div className='flex-1'>
            <div className='relative'>
              <Icon
                name='Search'
                size={20}
                className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground'
              />
              <input
                type='text'
                placeholder='Search by merchant name or ID...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className='w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e?.target?.value)}
            className='px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'
          >
            <option value='all'>All Status</option>
            <option value='active'>Active</option>
            <option value='suspended'>Suspended</option>
            <option value='trial'>Trial</option>
          </select>
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e?.target?.value)}
            className='px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'
          >
            <option value='all'>All Plans</option>
            <option value='Basic'>Basic</option>
            <option value='Professional'>Professional</option>
            <option value='Enterprise'>Enterprise</option>
          </select>
        </div>
      </div>

      {/* Subscriptions List */}
      <div className='flex-1 overflow-y-auto p-6'>
        <div className='space-y-4'>
          {filteredSubscriptions?.map((subscription) => (
            <div
              key={subscription?.id}
              className='bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow'
            >
              <div className='flex items-center justify-between'>
                <div className='flex items-center space-x-4'>
                  <div className='w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center'>
                    <Icon name='Building' size={24} className='text-primary' />
                  </div>
                  <div>
                    <h3 className='text-lg font-semibold text-foreground'>
                      {subscription?.merchantName}
                    </h3>
                    <p className='text-sm text-muted-foreground'>
                      ID: {subscription?.merchantId}
                    </p>
                    <div className='flex items-center space-x-2 mt-1'>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getPlanColor(subscription?.plan)}`}
                      >
                        {subscription?.plan}
                      </span>
                      <span className='text-xs text-muted-foreground'>
                        {subscription?.commissionRate}% commission
                      </span>
                    </div>
                  </div>
                </div>
                <div className='flex items-center space-x-3'>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(subscription?.status)}`}
                  >
                    {subscription?.status?.charAt(0)?.toUpperCase() +
                      subscription?.status?.slice(1)}
                  </span>
                  <div className='flex items-center space-x-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() =>
                        handleSubscriptionAction(subscription, 'details')
                      }
                    >
                      <Icon name='Eye' size={16} />
                    </Button>
                    {subscription?.pendingPayout > 0 && (
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() =>
                          handleSubscriptionAction(subscription, 'payout')
                        }
                        className='text-green-600 hover:text-green-700 border-green-200 hover:border-green-300'
                      >
                        <Icon name='IndianRupee' size={16} />
                        Payout
                      </Button>
                    )}
                    {subscription?.status === 'active' ? (
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() =>
                          handleSubscriptionAction(subscription, 'suspend')
                        }
                        className='text-red-600 hover:text-red-700 border-red-200 hover:border-red-300'
                      >
                        <Icon name='Pause' size={16} />
                        Suspend
                      </Button>
                    ) : (
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() =>
                          handleSubscriptionAction(subscription, 'activate')
                        }
                        className='text-green-600 hover:text-green-700 border-green-200 hover:border-green-300'
                      >
                        <Icon name='Play' size={16} />
                        Activate
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Financial Details Grid */}
              <div className='mt-4 grid grid-cols-4 gap-4 text-sm'>
                <div className='bg-background p-3 rounded-lg border border-border'>
                  <div className='text-muted-foreground text-xs'>
                    Monthly Fee
                  </div>
                  <div className='text-lg font-semibold text-foreground'>
                    ${subscription?.monthlyFee}
                  </div>
                </div>
                <div className='bg-background p-3 rounded-lg border border-border'>
                  <div className='text-muted-foreground text-xs'>
                    Total Revenue
                  </div>
                  <div className='text-lg font-semibold text-foreground'>
                    ${subscription?.totalRevenue?.toLocaleString()}
                  </div>
                </div>
                <div className='bg-background p-3 rounded-lg border border-border'>
                  <div className='text-muted-foreground text-xs'>
                    Pending Payout
                  </div>
                  <div className='text-lg font-semibold text-orange-600'>
                    ${subscription?.pendingPayout}
                  </div>
                </div>
                <div className='bg-background p-3 rounded-lg border border-border'>
                  <div className='text-muted-foreground text-xs'>
                    Commission Rate
                  </div>
                  <div className='text-lg font-semibold text-blue-600'>
                    {subscription?.commissionRate}%
                  </div>
                </div>
              </div>

              {/* Subscription Details */}
              <div className='mt-4 grid grid-cols-3 gap-4 text-sm'>
                <div>
                  <span className='text-muted-foreground'>Start Date:</span>
                  <span className='ml-2 text-foreground'>
                    {subscription?.startDate}
                  </span>
                </div>
                <div>
                  <span className='text-muted-foreground'>Next Billing:</span>
                  <span className='ml-2 text-foreground'>
                    {subscription?.nextBilling || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className='text-muted-foreground'>Last Payout:</span>
                  <span className='ml-2 text-foreground'>
                    {subscription?.lastPayout}
                  </span>
                </div>
              </div>

              <div className='mt-3'>
                <span className='text-muted-foreground text-sm'>
                  Payout Schedule:
                </span>
                <span className='ml-2 text-foreground text-sm capitalize'>
                  {subscription?.payoutSchedule}
                </span>
              </div>
            </div>
          ))}
        </div>

        {filteredSubscriptions?.length === 0 && (
          <div className='text-center py-12'>
            <Icon
              name='CreditCard'
              size={48}
              className='mx-auto text-muted-foreground mb-4'
            />
            <h3 className='text-lg font-medium text-foreground mb-2'>
              No subscriptions found
            </h3>
            <p className='text-muted-foreground'>
              Try adjusting your search or filter criteria.
            </p>
          </div>
        )}
      </div>

      {/* Action Modal */}
      {isModalOpen && selectedSubscription && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <div className='bg-card rounded-lg p-6 w-full max-w-md'>
            <h3 className='text-lg font-semibold text-foreground mb-4'>
              {modalType === 'suspend'
                ? 'Suspend Subscription'
                : modalType === 'activate'
                  ? 'Activate Subscription'
                  : modalType === 'payout'
                    ? 'Process Payout'
                    : 'Subscription Details'}
            </h3>

            <div className='mb-4'>
              <div className='flex items-center space-x-3 mb-3'>
                <div className='w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center'>
                  <Icon name='Building' size={20} className='text-primary' />
                </div>
                <div>
                  <div className='font-medium text-foreground'>
                    {selectedSubscription?.merchantName}
                  </div>
                  <div className='text-sm text-muted-foreground'>
                    {selectedSubscription?.plan} Plan
                  </div>
                </div>
              </div>
            </div>

            {modalType === 'suspend' && (
              <p className='text-muted-foreground mb-4'>
                Are you sure you want to suspend this merchant's subscription?
                They will lose access to premium features.
              </p>
            )}

            {modalType === 'activate' && (
              <p className='text-muted-foreground mb-4'>
                Are you sure you want to activate this merchant's subscription?
                Billing will resume next cycle.
              </p>
            )}

            {modalType === 'payout' && (
              <div className='mb-4'>
                <p className='text-muted-foreground mb-3'>
                  Process payout for this merchant's pending commissions.
                </p>
                <div className='bg-green-50 border border-green-200 rounded-lg p-3'>
                  <div className='text-sm text-green-800'>
                    <strong>Payout Amount:</strong> $
                    {selectedSubscription?.pendingPayout}
                  </div>
                </div>
              </div>
            )}

            {modalType === 'details' && (
              <div className='space-y-3 mb-4'>
                <div className='text-sm'>
                  <span className='text-muted-foreground'>Plan:</span>
                  <span className='ml-2 text-foreground'>
                    {selectedSubscription?.plan}
                  </span>
                </div>
                <div className='text-sm'>
                  <span className='text-muted-foreground'>Monthly Fee:</span>
                  <span className='ml-2 text-foreground'>
                    ${selectedSubscription?.monthlyFee}
                  </span>
                </div>
                <div className='text-sm'>
                  <span className='text-muted-foreground'>
                    Commission Rate:
                  </span>
                  <span className='ml-2 text-foreground'>
                    {selectedSubscription?.commissionRate}%
                  </span>
                </div>
                <div className='text-sm'>
                  <span className='text-muted-foreground'>Total Revenue:</span>
                  <span className='ml-2 text-foreground'>
                    ${selectedSubscription?.totalRevenue?.toLocaleString()}
                  </span>
                </div>
                <div className='text-sm'>
                  <span className='text-muted-foreground'>Pending Payout:</span>
                  <span className='ml-2 text-foreground'>
                    ${selectedSubscription?.pendingPayout}
                  </span>
                </div>
              </div>
            )}

            <div className='flex space-x-3'>
              <Button
                variant='outline'
                onClick={() => setIsModalOpen(false)}
                className='flex-1'
              >
                {modalType === 'details' ? 'Close' : 'Cancel'}
              </Button>
              {modalType !== 'details' && (
                <Button
                  onClick={confirmSubscriptionAction}
                  className={`flex-1 ${
                    modalType === 'suspend'
                      ? 'bg-red-600 hover:bg-red-700'
                      : modalType === 'payout'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-blue-600 hover:bg-blue-700'
                  } text-white`}
                >
                  {modalType === 'suspend'
                    ? 'Suspend'
                    : modalType === 'activate'
                      ? 'Activate'
                      : 'Process Payout'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialManagement;
